import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Loader2, Plus } from "lucide-react";
import { OwnCreativeDialog } from "@/components/social/OwnCreativeDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QueueCard } from "@/components/social/QueueCard";
import { HistoryList } from "@/components/social/HistoryList";
import { BestTimeCard } from "@/components/social/BestTimeCard";
import { MonthReport } from "@/components/social/MonthReport";
import { PostCalendar } from "@/components/social/PostCalendar";

export const SP_API = `${process.env.REACT_APP_BACKEND_URL}/api/social-planner`;
const monthLabel = (m) => { const t = new Date(`${m}-01T12:00:00`).toLocaleDateString("ro-RO", { month: "long", year: "numeric" }); return t.charAt(0).toUpperCase() + t.slice(1); };

export default function SocialPosts() {
  const [queue, setQueue] = useState(null);
  const [history, setHistory] = useState(null);
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [allHistory, setAllHistory] = useState([]);
  const [adding, setAdding] = useState(false);
  const [show, setShow] = useState(() => Number(localStorage.getItem("social_show_n")) || 3);
  const pickShow = (n) => { setShow(n); localStorage.setItem("social_show_n", String(n)); };
  const addPost = async (fd) => {
    try { await axios.post(`${SP_API}/queue/new`, fd); toast.success("Postare adăugată · text gata, Story în generare"); setAdding(false); loadQueue(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const loadQueue = () => axios.get(`${SP_API}/queue`).then((r) => setQueue(r.data.items || [])).catch((e) => { setQueue([]); toast.error("Programarea nu s-a încărcat: " + (e.response?.data?.detail || e.message)); });
  const loadHistory = (m = month) => axios.get(`${SP_API}/history`, { params: m ? { month: m } : {} }).then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
  useEffect(() => {
    loadQueue();
    axios.get(`${SP_API}/history`, { params: { limit: 300 } }).then((r) => setAllHistory(r.data.items || [])).catch(() => {});
    axios.get(`${SP_API}/history/months`).then((r) => setMonths(r.data.items || [])).catch(() => {});
    loadHistory("").then(() => axios.post(`${SP_API}/stats/refresh`).then((r) => r.data.updated && loadHistory("")).catch(() => {}));
  }, []);
  const generating = (queue || []).some((q) => q.story?.status === "generating");
  useEffect(() => {
    if (!generating) return;
    const t = setInterval(loadQueue, 6000);
    return () => clearInterval(t);
  }, [generating]);
  const pickMonth = (m) => { setMonth(m); setHistory(null); loadHistory(m); };

  return (
    <div className="space-y-6 p-6" data-testid="social-posts-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-zinc-100"><CalendarClock className="h-6 w-6 text-amber-400" /> Postări automate</h1>
        <p className="mt-1 text-sm text-zinc-400">Facebook + Instagram BRAGUS, postare + Story · implicit luni, miercuri și vineri la 10:00. Se publică exact ce vezi la „Viitoare”.</p>
        </div>
        <button data-testid="add-own-post-btn" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-amber-300"><Plus className="h-4 w-4" /> Postare nouă cu creativul meu</button>
      </div>
      {adding && <OwnCreativeDialog title="Postare nouă cu creativul tău" withSchedule onClose={() => setAdding(false)} onSubmit={addPost} />}
      <Tabs defaultValue="upcoming">
        <TabsList data-testid="social-tabs">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Viitoare</TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past">Anterioare</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar" className="pt-4">
          <PostCalendar queue={queue} history={allHistory} reload={loadQueue} />
        </TabsContent>
        <TabsContent value="upcoming" className="space-y-4 pt-4">
          <BestTimeCard />
          <p className="text-xs text-zinc-500">Rotație: luni produs (carusel cu pozele din Shopify), miercuri recenzie de 5 stele, vineri sfat de folosire; înaintea sărbătorilor, lunea devine ofertă de sezon.</p>
          {queue === null ? <Loader2 className="h-5 w-5 animate-spin text-zinc-500" /> : queue.length === 0
            ? <p className="text-sm text-zinc-500" data-testid="queue-empty">Nu sunt produse eligibile de programat (stoc ≥ 10, cu poză, nepostate în ultimele 30 de zile).</p>
            : <>
              <div className="mb-3 flex items-center gap-2 text-xs text-zinc-400" data-testid="show-count-picker">
                Arată postări viitoare:
                {[3, 6, 9].map((n) => <button key={n} data-testid={`show-count-${n}`} onClick={() => pickShow(n)} className={`rounded-full px-3 py-1 font-semibold ${show === n ? "bg-amber-400 text-zinc-950" : "border border-zinc-700 text-zinc-300 hover:border-amber-400"}`}>{n}</button>)}
                <span className="text-zinc-500">· Story-ul se face automat pentru primele 3</span>
              </div>
              <div className="grid gap-4 lg:grid-cols-3">{queue.filter((q) => q.status === "planned").slice(0, show).map((q) => <QueueCard key={q.id} q={q} reload={loadQueue} />)}</div>
            </>}
          {(queue || []).some((q) => q.moved_to) && (
            <p className="text-xs text-zinc-500" data-testid="moved-notes">{queue.filter((q) => q.moved_to).map((q) => `${q.slot_key}: mutată pe ${q.moved_to}`).join(" · ")}</p>
          )}
          {(queue || []).some((q) => q.status === "skipped" && !q.moved_to) && (
            <div className="mt-6 space-y-3">
              <p className="text-xs text-zinc-500">Zile sărite</p>
              <div className="grid gap-4 lg:grid-cols-3">{queue.filter((q) => q.status === "skipped" && !q.moved_to).map((q) => <QueueCard key={q.id} q={q} reload={loadQueue} />)}</div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="past" className="space-y-4 pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <select data-testid="history-month-select" value={month} onChange={(e) => pickMonth(e.target.value)} className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-200">
              <option value="">Toate lunile</option>
              {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
            <span className="text-xs text-zinc-500">like-uri, comentarii și distribuiri actualizate la deschidere și de 2 ori pe zi</span>
          </div>
          <MonthReport month={month} />
          <HistoryList items={history} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
